import React, { useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import {
  chatbotEvents,
  chatbotSelectors,
  getRentAmount,
  getCommissionInitialValues,
  getLeaseTermInitialValues,
} from 'modules/ui/ChatBot'
import { partyApiSelectors } from '../../../../api/party'
import { portfolioApiSelectors } from '../../../../api/portfolio'
import { dateUtils, formUtils } from '../../../../../utils'
import { Button } from '../../../../../views/components'
import { buildCommissionValidationObject } from '../../../utils/commissionUtils'
import { leaseTypes } from 'modules/api/portfolio/constants'
import CommissionSegment from '../CommissionSegment/CommissionSegment'
import styles from './UnmanagedCommissionForm.module.scss'
import { userApiSelectors } from '../../../../api/user'
import RouterPromptModal from 'components/organisms/Modals/RouterPromptModal/RouterPromptModal'

const validationSchema = yup.object().shape({
  procurementFee: buildCommissionValidationObject('procurementFee', ['once-off'], leaseTypes.unmanaged),
})

type UnmanagedCommissionFormProps = {
  portfolioId: string
  redirect: (url: string) => void
}

const UnmanagedCommissionForm = ({ portfolioId, redirect }: UnmanagedCommissionFormProps) => {
  const dispatch = useDispatch()
  const rentAmount = useSelector(getRentAmount)
  const initialValues = useSelector(getCommissionInitialValues)
  const currentPortfolioId = useSelector(chatbotSelectors.getCurrentPortfolioId)
  const { startDate, endDate } = useSelector(getLeaseTermInitialValues)
  const termInMonths = dateUtils.getTermInMonths(startDate, endDate)

  const agentResultSet = useSelector(state => partyApiSelectors.partiesResultsByTags(state)(['Agent', 'Agency']))
  // Wrap in a stable array so CommissionSegment/Search don't re-render on every dispatch.
  const defaultResultSets = useMemo(() => [agentResultSet], [agentResultSet])
  const isDraftPortfolio = useSelector(state => portfolioApiSelectors.isDraftPortfolio(state)(currentPortfolioId))
  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)
  const handleSubmit = useCallback(
    values => {
      // For unmanaged leases, only submit procurementFee, managementFee should be null
      dispatch(
        chatbotEvents.commissionValuesSubmitted({
          managementFee: null,
          procurementFee: values.procurementFee,
        }),
      )
    },
    [dispatch],
  )

  return (
    <div className={styles.root}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <fieldset disabled={isReadOnly}>
          <div className={styles.body}>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({
                setFieldTouched,
                setFieldValue,
                handleChange,
                handleBlur,
                submitForm,
                values,
                errors,
                submitCount,
                touched,
                dirty,
                isValid,
                isSubmitting,
              }) => {
                const getFieldError = formUtils.getFieldError(submitCount, touched, errors)

                return (
                  <>
                    <RouterPromptModal
                      when={dirty}
                      isValid={isValid}
                      isSubmitting={isSubmitting}
                      onSave={submitForm}
                      onDiscard={() => true}
                    />
                    <CommissionSegment
                      name="procurementFee"
                      label="Procurement fee"
                      rentAmount={rentAmount}
                      leaseType={leaseTypes.unmanaged}
                      leaseTermInMonths={termInMonths}
                      values={values.procurementFee}
                      defaultResultSets={defaultResultSets}
                      setFieldTouched={setFieldTouched}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      getFieldError={getFieldError}
                      errors={errors}
                    />
                    <div className={styles.footer}>
                      {isDraftPortfolio ? (
                        <>
                          <Button onClick={() => redirect(`/leases/${portfolioId}/edit/rent-and-fees`)}>Back</Button>
                          {/* A zero fee is a valid choice, so advancing only requires a valid form. */}
                          <Button type="button" disabled={Object.keys(errors).length > 0} onClick={submitForm}>
                            Next
                          </Button>
                        </>
                      ) : (
                        <Button type="button" disabled={!dirty || Object.keys(errors).length > 0} onClick={submitForm}>
                          Save
                        </Button>
                      )}
                    </div>
                  </>
                )
              }}
            </Formik>
          </div>
        </fieldset>
      </form>
    </div>
  )
}

export default UnmanagedCommissionForm
